import { supabase } from '@/lib/supabase';

export interface Achievement {
  id: string;
  name: string;
  description: string;
  points: number;
  badge_icon: string;
  category: string;
  earned_at?: string;
}

export interface UserAchievement {
  id: string;
  profile_id: string;
  achievement_type_id: string;
  earned_at: string;
  points_earned: number;
  metadata?: any;
  achievement?: Achievement;
}

export interface UserLevel {
  level: number;
  points: number;
  nextLevelPoints: number;
  progress: number; // percentage to next level
  title: string;
}

// Level titles
const LEVEL_TITLES = [
  'Newcomer',       // Level 1
  'Explorer',       // Level 2
  'Rising Star',    // Level 3
  'Professional',   // Level 4
  'Expert',         // Level 5
  'Master',         // Level 6+
];

// Points required for each level
const LEVEL_THRESHOLDS = [
  0,      // Level 1
  100,    // Level 2
  250,    // Level 3
  500,    // Level 4
  1000,   // Level 5
  2000,   // Level 6+
];

/**
 * Get user's achievements with error handling
 */
export async function getUserAchievements(profileId: string): Promise<UserAchievement[]> {
  if (!profileId) return [];
  
  try {
    const { data, error } = await supabase
      .from('user_achievements')
      .select(`
        *,
        achievement:achievement_types(*)
      `)
      .eq('profile_id', profileId)
      .order('earned_at', { ascending: false });
      
    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching user achievements:', error);
    return [];
  }
}

/**
 * Get all available achievements with error handling
 */
export async function getAllAchievements(): Promise<Achievement[]> {
  try {
    const { data, error } = await supabase
      .from('achievement_types')
      .select('*')
      .order('points', { ascending: false });
      
    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching achievements:', error);
    return [];
  }
}

/**
 * Get user's level information with improved error handling
 */
export async function getUserLevel(profileId: string): Promise<UserLevel | null> {
  if (!profileId) return null;
  
  try {
    // First check if the session is valid
    const { data: { session }, error: sessionError } = await supabase.auth.getSession();
    if (sessionError) throw sessionError;
    if (!session) return null;

    // Then fetch the profile data
    const { data, error } = await supabase
      .from('profiles')
      .select('level, points')
      .eq('id', profileId)
      .single();
      
    if (error) throw error;
    if (!data) return null;
    
    const level = data.level || 1;
    const points = data.points || 0;
    
    // Calculate next level threshold
    const currentLevelThreshold = level <= LEVEL_THRESHOLDS.length 
      ? LEVEL_THRESHOLDS[level - 1] 
      : LEVEL_THRESHOLDS[LEVEL_THRESHOLDS.length - 1];
      
    const nextLevelThreshold = level < LEVEL_THRESHOLDS.length 
      ? LEVEL_THRESHOLDS[level] 
      : currentLevelThreshold * 2;
    
    // Calculate progress to next level
    const pointsForCurrentLevel = points - currentLevelThreshold;
    const pointsRequiredForNextLevel = nextLevelThreshold - currentLevelThreshold;
    const progress = Math.min(Math.round((pointsForCurrentLevel / pointsRequiredForNextLevel) * 100), 100);
    
    // Get level title
    const titleIndex = Math.min(level - 1, LEVEL_TITLES.length - 1);
    const title = LEVEL_TITLES[titleIndex];
    
    return {
      level,
      points,
      nextLevelPoints: nextLevelThreshold,
      progress,
      title
    };
  } catch (error) {
    console.error('Error fetching user level:', error);
    return null;
  }
}

/**
 * Calculate profile completion percentage
 */
export function calculateProfileCompletion(profile: any): number {
  if (!profile) return 0;
  
  const requiredFields = [
    'full_name',
    'title',
    'summary',
    'email',
    'location'
  ];
  
  const optionalFields = [
    'phone',
    'website',
    'github'
  ];
  
  // Count filled required fields
  const filledRequired = requiredFields.filter(field => 
    profile[field] && profile[field].trim() !== ''
  ).length;
  
  // Count filled optional fields
  const filledOptional = optionalFields.filter(field => 
    profile[field] && profile[field].trim() !== ''
  ).length;
  
  // Calculate percentage (required fields count more)
  const requiredWeight = 0.7;
  const optionalWeight = 0.3;
  
  const requiredPercentage = (filledRequired / requiredFields.length) * requiredWeight * 100;
  const optionalPercentage = (filledOptional / optionalFields.length) * optionalWeight * 100;
  
  return Math.round(requiredPercentage + optionalPercentage);
}